import {
  ChatActionProposal,
  ChatActionStatus,
  ChatActionType,
  ChatQuickReplyOption,
  ChatQuickReplyStatus,
  ChatTicket,
  OrderCandidate,
  SupportTicketStatus,
  SupportTicketType,
} from '../../../types/supportTicket.types';
import { ConversationMessageEntity } from '../../../types/conversation.types';
import { formatDollars } from './chatFormat';

/**
 * Turning what the AI triage bot attached to a message into something a card
 * can render. See `types/supportTicket.types` for the domain itself.
 */

/**
 * Whether a message carries a card at all.
 *
 * Truthiness, not `!== null`: against an API build older than the triage
 * feature the two keys are absent rather than null, and `undefined` has to read
 * as "no card" rather than crash the thread.
 *
 * Yes / No buttons are not a card: they live inside the question's own bubble,
 * see `MessageBubble`.
 */
export const hasMessageCards = (message: ConversationMessageEntity) =>
  !!message.actionProposal || !!message.ticket;

const squash = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Whether the message text is the proposal's own sentence again.
 *
 * The bot writes its question into `body` as well as onto the card, so a
 * client that predates the cards still gets to read it. A client that draws
 * the card has no use for the copy: the card IS the question, and the same
 * sentence in a bubble above it reads as the bot asking twice. Compared
 * loosely - both are composed by the same backend code today, but a stray
 * space is no reason to show the question twice.
 */
export const bodyRepeatsCard = (message: ConversationMessageEntity) => {
  const summary = message.actionProposal?.summary;
  if (!summary || !message.body) return false;
  return squash(message.body) === squash(summary);
};

/**
 * A short name for the kind of action, over the bot's own sentence.
 *
 * The sentence (`summary`) is what the merchant is agreeing to and always
 * shows; this only tells them at a glance which of their settings is about to
 * move.
 */
/**
 * What the thread says while the assistant works.
 *
 * The backend sends only the phase; the words are ours. Anything unknown falls
 * back to the generic line, so a phase added later shows something true rather
 * than nothing.
 */
export const ASSISTANT_THINKING_LABEL: Record<string, string> = {
  reading: 'Reading your message...',
  checkingStatus: 'Checking your stores...',
  checkingMenu: 'Checking your menu...',
  checkingOrders: 'Looking up your orders...',
  preparing: 'Preparing your request...',
  /* The tracker's assistant, seen from admin. */
  checkingDelivery: 'Checking the delivery...',
};

export const assistantThinkingLabel = (state: string) =>
  ASSISTANT_THINKING_LABEL[state] ?? ASSISTANT_THINKING_LABEL.reading;

export const ACTION_TYPE_LABEL: Record<ChatActionType, string> = {
  pauseAll: 'Pause orders',
  unpauseAll: 'Resume orders',
  closingDay: 'Close for the day',
  workingHours: 'Update opening hours',
  prepTime: 'Update prep time',
  itemUnavailable: 'Pause menu item',
  itemAvailable: 'Bring menu item back',
  specialHours: 'Set special hours',
  reopenDay: 'Remove closing day',
  pauseProvider: 'Pause one platform',
  unpauseProvider: 'Resume one platform',
  cancelScheduledPause: 'Cancel scheduled pause',
};

/**
 * The kicker above the card's text.
 *
 * Falls back rather than printing nothing: the backend adds action types
 * before we hear about them, and `summary` alone already carries the card.
 */
export const actionTypeLabel = (actionType: ChatActionType) =>
  ACTION_TYPE_LABEL[actionType] ?? 'Request';

export const TICKET_TYPE_LABEL: Record<SupportTicketType, string> = {
  orderCancel: 'Cancel order',
  orderAdjustment: 'Order adjustment',
  refund: 'Refund',
  missingItem: 'Missing item',
  payout: 'Payout',
  menuIssue: 'Menu issue',
  other: 'Other',
};

/**
 * A ticket type in words. The raw type when it is one we do not know yet: the
 * backend grows this enum before we hear about it, and a ticket must still draw.
 */
export const ticketTypeLabel = (type: SupportTicketType) =>
  TICKET_TYPE_LABEL[type] ?? type;

/**
 * What a card shows instead of the raw status.
 *
 * `live` is the only one with buttons under it. Everything else is a sentence,
 * and `expired` is reachable two ways: the backend marks a stale proposal
 * within ten minutes, and the card gets there on its own the moment
 * `expiresAt` passes - see `actionCardView`.
 */
export type ActionCardView =
  | 'live'
  | 'working'
  | 'done'
  | 'failed'
  | 'declined'
  | 'expired'
  | 'superseded';

/**
 * Where a proposal stands right now, the clock included.
 *
 * The deadline is checked here rather than trusted from `status`, because a
 * card can sit on screen past it: the backend only sweeps expired proposals
 * every so often, and offering Confirm on a request that can no longer be
 * confirmed is a promise the button cannot keep. Confirming late is harmless
 * either way - the API answers 200 and does nothing.
 */
export const actionCardView = (
  proposal: ChatActionProposal,
  nowMs: number
): ActionCardView => {
  const byStatus: Record<
    Exclude<ChatActionStatus, 'pending'>,
    ActionCardView
  > = {
    confirmed: 'working',
    executed: 'done',
    failed: 'failed',
    rejected: 'declined',
    expired: 'expired',
    superseded: 'superseded',
  };

  if (proposal.status !== 'pending') return byStatus[proposal.status];
  return new Date(proposal.expiresAt).getTime() <= nowMs ? 'expired' : 'live';
};

/**
 * The chip a card wears once there is nothing left to press.
 *
 * Kept to a few words each: a chip is a solid block of colour, and a sentence
 * inside one wraps to two lines and reads as an alarm rather than a status.
 * Anything more to say goes in ACTION_VIEW_NOTE, under it.
 */
export const ACTION_VIEW_TEXT: Record<
  Exclude<ActionCardView, 'live'>,
  string
> = {
  working: 'Working on it...',
  done: 'Done',
  failed: "Couldn't complete",
  declined: 'Declined',
  expired: 'This request expired',
  superseded: 'Replaced by a newer request',
};

/** What the merchant should do about it, when the chip is not the whole story. */
export const ACTION_VIEW_NOTE: Partial<
  Record<Exclude<ActionCardView, 'live'>, string>
> = {
  failed: 'Support will take it from here.',
};

/**
 * What came of a question with buttons, for the admin.
 *
 * The merchant never sees this: for them the buttons are there or they are
 * not. Support reads the thread afterwards and wants to know the bot asked with
 * buttons and whether anybody pressed one - which answer it was is the very
 * next message, so it is not repeated here.
 */
export const QUICK_REPLY_STATUS_TEXT: Record<ChatQuickReplyStatus, string> = {
  /* The same words the admin's copy of a live proposal card uses. */
  open: 'Waiting for the merchant',
  answered: 'Answered',
  replaced: 'Asked again',
  /* Closed covers support stepping in as well as silence, and in both cases
     the merchant never answered the buttons - which is the part that is true
     either way. */
  closed: 'No answer',
};

/**
 * Whether the buttons are the Yes / No pair, the one set short enough to share
 * a row.
 *
 * Read off the keys only to lay the buttons out, never to decide what they do:
 * every key goes back to the backend as it came. Anything that is not the pair
 * - a location per button today, whatever comes next - is drawn as a list,
 * which holds a label of any length.
 */
export const isYesNoQuestion = (options: ChatQuickReplyOption[]) =>
  options.length > 0 &&
  options.every((option) => option.key === 'yes' || option.key === 'no');

/**
 * "Offered Yes / No", from the backend's own labels, or "Offered 3 buttons".
 *
 * A location picker is not spelled out: the bot's question above already lists
 * every location, and eight names and streets joined by slashes is a paragraph
 * under a paragraph.
 */
export const quickReplyOffer = (options: ChatQuickReplyOption[]) =>
  isYesNoQuestion(options)
    ? `Offered ${options.map((option) => option.label).join(' / ')}`
    : `Offered ${options.length} buttons`;

/**
 * A location's button label, split back into the name and the street.
 *
 * The backend writes them as one string joined by a spaced em dash, and leaves
 * the street off when a location has none. Split at the LAST dash, since a name
 * is likelier to carry one than a street. Anything without it - a brand,
 * "Pasta Lovers (DK Grill)" - is all name.
 */
export const splitChoiceLabel = (label: string) => {
  const at = label.lastIndexOf(' \u2014 ');
  if (at <= 0) return { name: label, detail: null };
  return {
    name: label.slice(0, at),
    detail: label.slice(at + 3).trim() || null,
  };
};

/**
 * The question's text without the choices it lists.
 *
 * Under "Which location is this for?" the backend writes every location into
 * the body too, one per line, for an app too old to draw the buttons. Where
 * the buttons are drawn they ARE that list, and the same names twice in one
 * bubble read as the bot asking twice - the rule `bodyRepeatsCard` applies to
 * cards. Only a line that is a whole label goes; everything else the bot wrote
 * stays, and a body with nothing left is shown as sent.
 */
export const bodyWithoutChoices = (
  body: string,
  options: ChatQuickReplyOption[]
) => {
  const labels = new Set(
    options.map((option) => squash(option.label)).filter(Boolean)
  );
  const rest = body
    .split('\n')
    .filter((line) => !labels.has(squash(line)))
    .join('\n')
    /* The blank line that set the list apart, now setting apart nothing. */
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return rest || body;
};

/**
 * The ticket's status, in the words of whoever is reading it.
 *
 * The merchant is told where their request is ("With support"); the admin is
 * told what is on their plate ("Open"). Same field, two audiences.
 */
export const ticketStatusLabel = (
  status: SupportTicketStatus,
  audience: 'merchant' | 'admin'
) => {
  if (audience === 'admin') {
    if (status === 'open') return 'Open';
    return status === 'claimed' ? 'In progress' : 'Resolved';
  }
  if (status === 'open') return 'With support';
  return status === 'claimed' ? 'Support is working on it' : 'Resolved';
};

/**
 * One candidate order as the two lines of its row: "#4521 · Ana" over
 * "Conroy · 7:04 PM · $24.50".
 *
 * The number and the customer are what the merchant knows an order by, so they
 * lead; the rest tells two of Ana's orders apart. Whatever the backend could
 * not fill in is left out rather than printed as a gap - a row of separators
 * around a missing name reads as broken data.
 */
export const orderCandidateLines = (candidate: OrderCandidate) => {
  const detail = [
    candidate.brandName,
    candidate.localTime,
    candidate.total === null ? null : formatDollars(candidate.total),
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    name: [
      /* The id off the Orders screen, not `number`: for AI orders and some
         providers that one is a UUID the merchant cannot read back. */
      `#${candidate.orderId}`,
      candidate.customerName,
    ]
      .filter(Boolean)
      .join(' · '),
    detail: detail || null,
  };
};

/** Whether the merchant still has an order to pick on this ticket. */
export const awaitsOrderChoice = (ticket: ChatTicket) =>
  ticket.orderId === null && ticket.orderCandidates.length > 0;
